import React, { useState, useRef, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import api from '../services/api';
import ReflectionTree from './ReflectionTree';
import useClickOutside from '../hooks/useClickOutside';
import { formatDateFull } from '../utils/dateUtils';
import { formatFileSize } from '../utils/formatFileSize';

function getReflectionMediaSize(reflection) {
  let total = 0;
  if (reflection.video_size) total += reflection.video_size;
  if (reflection.audio_size) total += reflection.audio_size;
  if (reflection.images?.length) {
    for (const img of reflection.images) {
      if (img.file_size) total += img.file_size;
    }
  }
  return total;
}

/**
 * ReflectionCard - A single reflection card with title, buttons, and thumbnail.
 * Clicking "Show" always opens the Studio view.
 * Used in both the Prompts view (inside expanded prompts) and the Reflections view.
 */
function ReflectionCard({
  reflection,
  isShared,
  isNew,
  isRecording,
  isAdmin,
  showPromptText,
  promptId,
  onDelete,
  onDismissBadge,
  onRecordingStateChange,
  onViewStatusChange,
  transcriptionPreference,
  currentUserId,
  onTranscriptionUpdate,
  onChangePrompt,
  onOpenStudio,
  showMediaSize = true,
  onEnhance,
  authorizeAI,
  enableFollowups,
  onOpenFollowup,
  onTranscribeAndEnhance,
  lastEnhancementUpdate,
  initialTreeExpanded = false,
  isMatch,
  searchText,
  timezone
}) {
  const isDisabledByRecording = isRecording;
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [transcribeError, setTranscribeError] = useState(null);
  const [showContextMenu, setShowContextMenu] = useState(false);
  const [menuPosition, setMenuPosition] = useState(null);
  const menuRef = useRef(null);
  const moreButtonRef = useRef(null);

  // Track enhancement completing (generating → completed) to auto-expand tree
  const [enhancementCompletedCount, setEnhancementCompletedCount] = useState(0);
  const prevAiFollowupStatusRef = useRef(reflection.ai_followup_status);
  useEffect(() => {
    if (prevAiFollowupStatusRef.current === 'generating' && reflection.ai_followup_status === 'completed') {
      setEnhancementCompletedCount(prev => prev + 1);
    }
    prevAiFollowupStatusRef.current = reflection.ai_followup_status;
  }, [reflection.ai_followup_status]);

  // Close context menu when clicking outside
  const contextMenuRefs = [moreButtonRef, menuRef];
  const closeContextMenu = useCallback(() => setShowContextMenu(false), []);
  useClickOutside(contextMenuRefs, closeContextMenu, showContextMenu);

  // Close context menu on scroll (position would be stale)
  useEffect(() => {
    if (!showContextMenu) return;
    const handleScroll = () => setShowContextMenu(false);
    window.addEventListener('scroll', handleScroll, true);
    return () => window.removeEventListener('scroll', handleScroll, true);
  }, [showContextMenu]);

  // Check if reflection has media that can be transcribed
  const hasVideo = !!reflection.video_url;
  const hasAudio = !!reflection.audio_url;
  const hasMedia = hasVideo || hasAudio;

  // Get individual transcription statuses
  const videoStatus = reflection.video_transcription_status || 'none';
  const audioStatus = reflection.audio_transcription_status || 'none';

  // Determine overall transcription status for display
  const getOverallStatus = () => {
    const statuses = [];
    if (hasVideo) statuses.push(videoStatus);
    if (hasAudio) statuses.push(audioStatus);

    if (statuses.some(s => s === 'processing')) return 'processing';
    if (statuses.every(s => s === 'completed')) return 'completed';
    if (statuses.some(s => s === 'failed')) return 'failed';
    if (statuses.some(s => s === 'none')) return 'none';
    return 'none';
  };

  const overallStatus = getOverallStatus();
  const isOwner = reflection.user_id === currentUserId;
  const hasTextContent = !!(reflection.text_content?.trim() || reflection.video_transcription_text?.trim() || reflection.audio_transcription_text?.trim());
  const canTranscribe = hasMedia && isOwner && !isShared && transcriptionPreference !== 'never';

  // Show transcribe button if any media needs transcription
  const needsTranscription = (hasVideo && (videoStatus === 'none' || videoStatus === 'failed')) ||
                             (hasAudio && (audioStatus === 'none' || audioStatus === 'failed'));
  const showTranscribeButton = canTranscribe && needsTranscription;

  const handleTranscribe = async (e) => {
    e.stopPropagation();
    if (isDisabledByRecording || isTranscribing) return;

    setIsTranscribing(true);
    setTranscribeError(null);

    try {
      await api.post(
        `/reflections/${reflection.id}/transcribe`,
        {}
      );

      if (onTranscriptionUpdate) {
        // Update status for both media types if they exist
        const updates = { ...reflection };
        if (hasVideo) updates.video_transcription_status = 'processing';
        if (hasAudio) updates.audio_transcription_status = 'processing';
        onTranscriptionUpdate(updates);
      }
    } catch (err) {
      console.error('Error starting transcription:', err);
      setTranscribeError(err.response?.data?.error || 'Failed to start transcription');
    } finally {
      setIsTranscribing(false);
    }
  };

  // Open a reflection from the tree by fetching its full data
  const handleOpenTreeReflection = async (node) => {
    const nodeIsReadOnly = node.user_id !== currentUserId;
    if (node.id === reflection.id) {
      onOpenStudio(reflection, nodeIsReadOnly, { sourceCardId: reflection.id, fromTree: true });
      return;
    }
    try {
      const res = await api.get(`/reflections/${node.id}`);
      onOpenStudio(res.data, nodeIsReadOnly, { sourceCardId: reflection.id, fromTree: true });
    } catch (err) {
      console.error('Error opening reflection from tree:', err);
    }
  };

  const titleGenerationStatus = reflection.title_generation_status;
  const titleGenerationSource = reflection.title_generation_source;

  const getStatusText = () => {
    // Enhancement status
    if (reflection.ai_followup_status === 'generating') {
      return 'Generating follow-ups...';
    }
    // Title generation takes priority in display
    if (titleGenerationStatus === 'generating') {
      return 'Generating Title...';
    }
    // Show title generated for text-only sources
    if (titleGenerationStatus === 'completed' && titleGenerationSource === 'text') {
      return 'Title Generated';
    }
    // For transcription flow
    if (isTranscribing || overallStatus === 'processing') {
      return 'Transcribing...';
    }
    if (overallStatus === 'completed') {
      return 'Transcribed';
    }
    if (overallStatus === 'failed') {
      return 'Transcription failed';
    }
    return null;
  };

  // Render context menu via portal so it escapes all stacking contexts
  const contextMenuPortal = showContextMenu && menuPosition ? ReactDOM.createPortal(
    <div
      ref={menuRef}
      className="reflection-context-menu"
      style={{
        position: 'fixed',
        top: menuPosition.top,
        right: menuPosition.right,
        left: 'auto',
        zIndex: 99999
      }}
    >
      {showTranscribeButton && (
        <button
          className="reflection-context-menu-item"
          onClick={(e) => {
            handleTranscribe(e);
            setShowContextMenu(false);
          }}
          disabled={isDisabledByRecording || isTranscribing}
          title="Use AI to translate the video/audio to text"
        >
          {isTranscribing ? 'Transcribing...' : (overallStatus === 'failed' ? 'Retry Transcribe' : 'Transcribe')}
        </button>
      )}
      {isOwner && authorizeAI && enableFollowups && onEnhance && hasTextContent && reflection.ai_followup_status !== 'generating' && (
        <button
          className="reflection-context-menu-item"
          onClick={(e) => {
            e.stopPropagation();
            onEnhance(reflection.id);
            setShowContextMenu(false);
          }}
          disabled={isDisabledByRecording}
          title="Generate AI follow-up questions to deepen this reflection"
        >
          {reflection.ai_followup_suggestions?.length > 0 ? 'New Questions' : 'Enhance'}
        </button>
      )}
      {onChangePrompt && (
        <button
          className="reflection-context-menu-item"
          onClick={(e) => {
            e.stopPropagation();
            if (isDisabledByRecording) return;
            onChangePrompt(reflection);
            setShowContextMenu(false);
          }}
          disabled={isDisabledByRecording}
          title="Move this reflection to a different prompt"
        >
          Change Prompt
        </button>
      )}
      <button
        className="reflection-context-menu-item reflection-context-menu-item-danger"
        onClick={(e) => {
          e.stopPropagation();
          if (isDisabledByRecording) return;
          onDelete(reflection);
          setShowContextMenu(false);
        }}
        disabled={isDisabledByRecording}
        title="Permanently delete this reflection"
      >
        Delete
      </button>
    </div>,
    document.body
  ) : null;

  return (
    <div
      data-reflection-id={reflection.id}
      className="reflection-inline-card"
    >
      {/* NEW Badge for shared reflections */}
      {isNew && (
        <div
          className="new-badge"
          onClick={(e) => onDismissBadge(reflection.id, e)}
          title="Click to dismiss"
        >
          NEW
        </div>
      )}

      {/* Match Badge for search results */}
      {isMatch && (
        <div className={`match-badge ${isNew ? 'with-new' : ''}`}>
          Match
        </div>
      )}

      {/* Prompt text display (only in reflections view) */}
      {showPromptText && (
        <div className="reflection-prompt-text">
          {reflection.prompt_text}
        </div>
      )}

      {/* Title and metadata display */}
      <div className="reflection-inline-note">
        <div>{reflection.title || 'No title'}</div>
        <div style={{ fontStyle: 'italic', fontSize: '0.85rem', color: 'var(--color-text-secondary)', marginTop: '0.25rem', display: 'flex', justifyContent: 'space-between' }}>
          <span>{formatDateFull(reflection.updated_at, timezone)}</span>
          {showMediaSize && getReflectionMediaSize(reflection) > 0 && (
            <span style={{ opacity: 0.7 }}>{formatFileSize(getReflectionMediaSize(reflection))}</span>
          )}
        </div>
        {/* Status - shown underneath the date (transcription or title generation) */}
        {getStatusText() && (
          <div style={{
            fontSize: '0.85rem',
            color: overallStatus === 'failed' ? 'var(--color-danger)' :
                   titleGenerationStatus === 'generating' ? 'var(--color-primary)' :
                   titleGenerationStatus === 'completed' ? 'var(--color-success)' :
                   'var(--color-text-secondary)',
            fontStyle: 'italic',
            fontWeight: (isTranscribing || overallStatus === 'processing' || titleGenerationStatus === 'generating') ? 'bold' : 'normal',
            marginTop: '0.25rem'
          }}>
            {getStatusText()}
            {transcribeError && (
              <span style={{ marginLeft: '0.5rem', color: 'var(--color-danger)' }}>
                - {transcribeError}
              </span>
            )}
          </div>
        )}
        {isAdmin && (
          <div style={{ fontStyle: 'italic', fontSize: '0.85rem', color: 'var(--color-text-secondary)', marginTop: '0.25rem' }}>
            Reflection id: {reflection.id}
          </div>
        )}
      </div>

      <div className="reflection-inline-buttons">
        <button
          className="reflection-btn-show"
          onClick={(e) => {
            e.stopPropagation();
            if (isDisabledByRecording) return;
            onOpenStudio(reflection, !isOwner, { sourceCardId: reflection.id });
          }}
          disabled={isDisabledByRecording}
          style={{
            cursor: isDisabledByRecording ? 'not-allowed' : 'pointer',
            opacity: isDisabledByRecording ? 0.5 : 1
          }}
        >
          Show
        </button>
        {isOwner && (
          <div className="reflection-context-menu-container">
            <button
              ref={moreButtonRef}
              className="reflection-btn-more"
              onClick={(e) => {
                e.stopPropagation();
                if (isDisabledByRecording) return;
                if (!showContextMenu && moreButtonRef.current) {
                  const rect = moreButtonRef.current.getBoundingClientRect();
                  setMenuPosition({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
                }
                setShowContextMenu(!showContextMenu);
              }}
              disabled={isDisabledByRecording}
              style={{
                cursor: isDisabledByRecording ? 'not-allowed' : 'pointer',
                opacity: isDisabledByRecording ? 0.5 : 1
              }}
              title="More actions"
            >
              &#8942;
            </button>
          </div>
        )}
      </div>

      <div className="reflection-inline-thumbnail">
        {reflection.video_url ? (
          reflection.video_thumbnail_url ? (
            <img
              src={reflection.video_thumbnail_url}
              alt="Video thumbnail"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            <video src={reflection.video_url} />
          )
        ) : reflection.images?.length > 0 ? (
          <img
            src={reflection.images[0].image_url}
            alt={reflection.images[0].title || 'Image'}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : reflection.audio_url ? (
          <div className="reflection-thumbnail-placeholder">🎤</div>
        ) : reflection.text_content ? (
          <div className="reflection-thumbnail-placeholder">📝</div>
        ) : (
          <div className="reflection-thumbnail-placeholder">📄</div>
        )}
      </div>

      {isShared && (
        <div className="reflection-shared-from">
          Shared from {reflection.created_by}
        </div>
      )}

      {/* Follow-up question indicator (this is a child reflection) */}
      {reflection.followup_question && (
        <div className="reflection-followup-indicator">
          Follow-up: {reflection.followup_question}
        </div>
      )}

      {/* Reflection tree (parent-child hierarchy) - wrapper ensures grid places it at the bottom
          IMPORTANT: When enableFollowups is OFF, the tree must still show if there are answered
          followup reflections (real child nodes). Only unanswered placeholder "?" nodes are hidden.
          The tree should only be fully hidden when there are zero actual child reflections.
          Do NOT gate the entire tree behind enableFollowups — ask the user before changing this. */}
      {(reflection.parent_reflection_id ||
        reflection.ai_followup_suggestions?.some(s => s.answered) ||
        (enableFollowups && (reflection.ai_followup_suggestions?.length > 0 || reflection.ai_followup_status === 'generating'))) && (
        <div className="reflection-tree-wrapper">
          <ReflectionTree
            reflectionId={reflection.id}
            onOpenReflection={handleOpenTreeReflection}
            enableFollowups={enableFollowups}
            followupStatus={reflection.ai_followup_status}
            enhancementCompleted={enhancementCompletedCount}
            onEnhance={onEnhance}
            onOpenFollowup={onOpenFollowup}
            authorizeAI={authorizeAI}
            onTranscribeAndEnhance={onTranscribeAndEnhance}
            lastEnhancementUpdate={lastEnhancementUpdate}
            initialExpanded={initialTreeExpanded}
            currentUserId={currentUserId}
            searchText={searchText}
            timezone={timezone}
          />
        </div>
      )}

      {contextMenuPortal}
    </div>
  );
}

export default ReflectionCard;
